import { getFileDownloadAsFileDataByKey } from 'store/files/services/getFileDownloadAsFileDataByKey.service';
import { getFileDownloadAsFileSignedUrlByFilePermission } from 'store/files/services/getFileDownloadAsFileSignedUrlByFilePermission.service';
import { fetchPrivateProjectBankFile } from 'utils/fetchPrivateProjectBankFile';
import { getPrivateProjectBankArchiveDownload } from '../services/private-project-bank/getPrivateProjectBankArchiveDownload.service';

/**
 * Downloads a url whose response carries Content-Disposition: attachment
 * (e.g. the archive job's presigned S3 urls) by navigating the CURRENT tab to
 * it: the browser starts the download and stays on the page. Unlike a
 * programmatic click on a target="_blank" anchor, this is not subject to
 * popup blocking — which matters because the archive job finishes long after
 * the user's click gesture has expired.
 */
export const triggerSameTabDownload = (url) => {
  window.location.assign(url);
};

export const triggerBrowserDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || '';
  link.target = '_blank';

  document.body.appendChild(link);
  // Dispatching a MouseEvent instead of link.click() because plain click()
  // does not work on the latest Firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );
  document.body.removeChild(link);
};

export const downloadPublicProjectBankFile = async (file) => {
  const response = await getFileDownloadAsFileSignedUrlByFilePermission(
    file.id,
    file.name
  );

  if (!response?.url) {
    throw new Error('Failed to get public file download URL');
  }

  triggerBrowserDownload(response.url, response.fileName || file.name);
};

/**
 * Project bank "forms" items are virtual (no FilePermission rows), so their
 * PDFs are fetched by S3 key. The backend falls back to the legacy
 * `{businessId}_{projectKey}_pdfs` path for older form PDFs.
 */
export const downloadProjectBankFormFile = async (file) => {
  const response = await getFileDownloadAsFileDataByKey(file.key, file.name);

  if (!response?.url) {
    throw new Error('Failed to get form file download URL');
  }

  triggerBrowserDownload(response.url, response.fileName || file.name);
};

export const downloadPrivateProjectBankFile = async (file) => {
  const blobUrl = await fetchPrivateProjectBankFile({
    projectId: file.projectId,
    fileId: file.id,
    blobUrl: true,
  });

  if (!blobUrl) {
    throw new Error('Failed to get private file download data');
  }

  triggerBrowserDownload(blobUrl, file.name);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

// Private bank only: its folders are small enough for the synchronous server
// zip. The public bank goes through the background archive job instead
// (startProjectBankArchiveJob + pollProjectBankArchiveJob).
export const requestPrivateProjectBankArchiveDownload = async ({
  projectId,
  fileIds = [],
  folderIds = [],
}) => {
  if (!projectId) {
    throw new Error('Missing project id for archive download');
  }

  const response = await getPrivateProjectBankArchiveDownload({
    projectId,
    fileIds,
    folderIds,
  });

  if (!response?.downloadUrl) {
    throw new Error('Failed to get archive download URL');
  }

  return response;
};
